import { Button } from "@/components/custom/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { client } from "@/client";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import CronInput from "@/components/custom/cron-input.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Route } from "@/routes/dashboard/reports/$reportId/route.tsx";
import {
  getTimeZone,
  TimeSettings,
  useTimeSettings,
} from "@/state/time-settings.tsx";
import React, { useMemo } from "react";
import SelectInputCreatable from "@/components/select-input/select-input-creatable";
import { TzPicker } from "@/routes/dashboard/-tz-picker.tsx";
import { MdWarning } from "react-icons/md";
import {
  DialogPage,
  MultiStepDialogContent,
} from "@/components/custom/paginated-dialog-content/multi-step-dialog-content.tsx";
import { usePermissions } from "@/hooks/use-permissions";
import { CheckboxGroupInput } from "@/components/custom/form-inputs/checkbox-group.tsx";
import { Switch } from "@/components/ui/switch.tsx";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group.tsx";
import { Loader } from "@/components/custom/loader.tsx";
import { resolveQuestions, questionsToOptions } from "./-question-utils";

import { showActionErrorToast } from "@/state/error-toast";
import { showEmailOutcomeToast } from "@/state/email-outcome-toast";

const optionSchema = z.object({
  label: z.string(),
  value: z.string(),
});
const reportFormSchema = z.object({
  reportName: z.string().min(1, { message: "Please enter a report name" }),
  mailingList: z.array(optionSchema),
  schedule: z.string().min(1, { message: "Please select a schedule" }),
  timezone: z
    .string()
    .min(1, { message: "Please select a timezone" })
    .optional(),
  addAttachments: z.boolean(),
  fileType: z.string().optional(),
  selectedQuestions: z.array(z.string()).optional(),
});

interface CreateSubscriptionProps {
  filters: string;
  onClose?: () => void;
}

export const CreateSubscription = ({
  filters,
  onClose,
}: CreateSubscriptionProps) => {
  const { reportId } = Route.useRouteContext();
  const timeSettings = useTimeSettings();
  const { t } = useTranslation();
  const { permissions } = usePermissions();
  const [currentStep, setCurrentStep] = React.useState(0);

  const params = new URLSearchParams(filters);
  const dateRangeRegex = /^\d{4}-\d{2}-\d{2}(~\d{4}-\d{2}-\d{2})?$/;

  const isAbsDates = Array.from(params.entries()).some(([key, value]) => {
    return key.includes("date") && dateRangeRegex.test(value ?? "");
  });

  const subscribe = useMutation({
    ...client.emailReports.createSchedule.asMutationOptions(),
    onError: (err) => showActionErrorToast(err),
    onSuccess: () => toast.success(t("successfullyCreatedSubscription")),
  });
  const testSubscription = useMutation({
    ...client.reporting.sendTestReport.asMutationOptions(),
    onError: (err) => showActionErrorToast(err),
    onSuccess: (outcome) =>
      showEmailOutcomeToast(outcome, {
        partial: t("testEmailsPartiallyFailed"),
        success: t("mailSent"),
      }),
  });
  const form = useForm<z.infer<typeof reportFormSchema>>({
    resolver: zodResolver(reportFormSchema),
    defaultValues: {
      reportName: "",
      mailingList: [],
      schedule: "0 9 * * MON", // Default to 9 AM every Monday
      timezone: getTimeZone("local"),
      addAttachments: false,
      fileType: "csv",
    },
  });

  const reportName = form.watch("reportName");
  const mailingList = form.watch("mailingList");
  const addAttachments = form.watch("addAttachments");

  const { data } = useQuery({
    ...client.reporting.getReportMetadata.asQueryOptions({
      id: reportId,
      questionTypes: ["pivot", "table"],
    }),
    enabled: !!permissions?.reports.subscription.addAttachments,
  });
  // Captured here because `onSubmit`'s parameter shadows the outer `data`.
  const metadataQuestions = data?.questions;
  const questions = questionsToOptions(metadataQuestions ?? []);

  const onSubmit = async (data: z.infer<typeof reportFormSchema>) => {
    const body = {
      name: "Next Email Report",
      cronStart: data?.schedule,
      timezone: data?.timezone ?? timeSettings.timeZone ?? "UTC",
      data: {
        reportName: data.reportName,
        mailingList: data.mailingList.map((option) => option.value),
        reportId,
        reportUrl: filters,
        addAttachments: data.addAttachments,
        ...(data.addAttachments && {
          fileType: data.fileType,
          selectedQuestions: resolveQuestions(
            data.selectedQuestions,
            metadataQuestions,
          ),
        }),
      },
    };
    try {
      await subscribe.mutateAsync({
        requestBody: body,
      });
      onClose?.();
    } catch (error) {
      // stay open
    }
  };
  const sendTestMail = async () => {
    const formValues = form.getValues();
    const body = {
      mailingList: mailingList.map((option) => option.value),
      reportName,
      reportId,
      filters,
      addAttachments: formValues.addAttachments,
      ...(addAttachments && {
        fileType: formValues.fileType,
        selectedQuestions: resolveQuestions(
          formValues.selectedQuestions,
          metadataQuestions,
        ),
      }),
    };
    await testSubscription.mutateAsync({
      requestBody: body,
    });
  };

  const emailQueryResult = useQuery(
    client.users.listEmails.asQueryOptions({ page: 1, limit: 9999 }),
  );
  const emailOptions = React.useMemo(
    () =>
      (emailQueryResult.data?.results ?? []).map((value: string) => ({
        value,
        label: value,
      })),
    [emailQueryResult.data?.results],
  );

  const Page1 = useMemo(
    () => (
      <div className="space-y-4">
        <FormField
          control={form.control}
          name="reportName"
          render={({ field }) => (
            <FormItem className="py-4">
              <FormLabel>{t("name")}</FormLabel>
              <FormControl>
                <Input placeholder={t("enterName")} {...field} />
              </FormControl>
              <FormMessage>
                {form.formState.errors.reportName?.message}
              </FormMessage>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="mailingList"
          render={({ field }) => (
            <FormItem className="py-4">
              <FormLabel>{t("inviteeList")}</FormLabel>
              <FormControl>
                <SelectInputCreatable
                  {...field}
                  isMulti={true}
                  options={emailOptions}
                  createLabel="Invite"
                  placeholder={t("typeEmails")}
                />
              </FormControl>
              <FormMessage>
                {form.formState.errors.mailingList?.message}
              </FormMessage>
            </FormItem>
          )}
        />
      </div>
    ),
    [form.control, t, emailOptions],
  );

  const Page2 = useMemo(
    () => (
      <div className="space-y-4">
        <FormField
          key={`schedule-${currentStep}`} //needed to get react-hook-form to re-render
          control={form.control}
          name="schedule"
          render={({ field }) => (
            <FormItem className="py-4">
              <FormLabel>{t("schedule")}</FormLabel>
              <FormControl>
                <CronInput className="gap-4" {...field} />
              </FormControl>
              <FormMessage>
                {form.formState.errors.schedule?.message}
              </FormMessage>
            </FormItem>
          )}
        />
        <div className={"flex items-center gap-4"}>
          <label className="text-nowrap">{t("atTimezone")}</label>
          <FormField
            key={`timezone-${currentStep}`}
            control={form.control}
            name="timezone"
            render={({ field }) => (
              <TzPicker
                className="w-full"
                value={field.value as TimeSettings["timezone"]}
                onChange={(tz) => field.onChange(tz)}
              />
            )}
          />
        </div>
      </div>
    ),
    [form.control, t],
  );

  const Page3 = useMemo(
    () => (
      <div className="space-y-6">
        <div className="space-y-3">
          <FormField
            control={form.control}
            name="addAttachments"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center gap-2">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="!mt-0 whitespace-nowrap">
                  {t("attachResults")}
                </FormLabel>
                <FormMessage>
                  {form.formState.errors.addAttachments?.message}
                </FormMessage>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="fileType"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ToggleGroup
                    type="single"
                    defaultValue="csv"
                    {...field}
                    onValueChange={field.onChange}
                    disabled={!addAttachments}
                    className="justify-start py-2"
                  >
                    <ToggleGroupItem value="csv">.csv</ToggleGroupItem>
                    <ToggleGroupItem value="xlsx">.xlsx</ToggleGroupItem>
                  </ToggleGroup>
                </FormControl>
                <FormMessage>
                  {form.formState.errors.fileType?.message}
                </FormMessage>
              </FormItem>
            )}
          />
        </div>

        <hr />

        {questions.length === 0 ? (
          <div className="m-4 p-4 rounded-lg">
            <MdWarning className="inline-block mr-2 h-5 w-5" />
            {t("noQuestionsAvailable")}
          </div>
        ) : (
          <FormField
            control={form.control}
            name="selectedQuestions"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <CheckboxGroupInput
                    {...field}
                    options={questions}
                    disabled={!addAttachments}
                  />
                </FormControl>
                <FormMessage>
                  {form.formState.errors.mailingList?.message}
                </FormMessage>
              </FormItem>
            )}
          />
        )}
      </div>
    ),
    [form.control, addAttachments, questions, t],
  );

  const testMailButton = useMemo(
    () => (
      <Button
        type="button"
        onClick={sendTestMail}
        variant="azureOutline"
        disabled={testSubscription.isPending}
      >
        {testSubscription.isPending && <Loader size="xs" />}
        {t("sendMailNow")}
      </Button>
    ),
    [sendTestMail, t],
  );

  const pages: DialogPage[] = useMemo(
    () =>
      [
        {
          title: t("overview"),
          content: Page1,
          fields: ["reportName", "mailingList"],
        },
        {
          title: t("scheduler"),
          content: Page2,
          footer: testMailButton,
          fields: ["schedule", "timezone"],
        },
        permissions.reports.subscription.addAttachments && {
          title: t("additionalOptions"),
          content: Page3,
          footer: testMailButton,
          fields: ["addAttachments", "fileType", "selectedQuestions"],
        },
      ].filter(Boolean) as DialogPage[],
    [
      Page1,
      Page2,
      Page3,
      testMailButton,
      permissions.reports.subscription.addAttachments,
      t,
    ],
  );

  const handleNext = React.useCallback(async () => {
    const fields = pages[currentStep].fields;
    const isStepValid = await form.trigger(
      fields as (keyof z.infer<typeof reportFormSchema>)[],
      { shouldFocus: true },
    );

    if (isStepValid && !isAbsDates) {
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, pages, form]);

  const handleBack = React.useCallback(() => {
    setCurrentStep((prev) => prev - 1);
  }, []);

  return (
    <>
      <Form {...form}>
        {isAbsDates && (
          <div className="m-4 p-4 rounded-lg bg-warning text-warning-foreground">
            <MdWarning className="inline-block mr-2 h-5 w-5" />
            {t("absDatesWarning")}
          </div>
        )}
        <form>
          <MultiStepDialogContent
            title={t("createSubscription")}
            pages={pages}
            currentStep={currentStep}
            onNext={handleNext}
            onBack={handleBack}
            onSubmit={form.handleSubmit(onSubmit)}
          />
        </form>
      </Form>
    </>
  );
};
